import { ref, onMounted } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import { injectService } from '@patsnap/tahiti-core';
import { useDisposes } from '@telecom/shared/composables/use-disposes';
import { UserInfoService } from '@telecom/shared/domain/permission';
import { isUserLogin } from '@telecom/shared/startup/user-auth';

/**
 * 用户信息组合式函数
 *
 * 提供用户相关的信息和状态，包括：
 * - 判断是否为个人租户
 * - 判断是否为企业租户
 * - 判断是否为 Pro 用户
 * - 判断是否为免费用户
 * - 判断是否有积分套餐权益
 * - 用户订阅信息
 */
export const useUserInfo = createSharedComposable(() => {
    const userInfoService = injectService<UserInfoService>(UserInfoService);
    const { disposes } = useDisposes();

    const isLogin = ref(false);
    const isLoginResolved = ref(false);
    const currentSubscription = ref(userInfoService.getCurrentSubscription());
    const isLoadingSubscription = ref(userInfoService.isLoadingSubscription());
    const isEnterpriseTenant = ref(userInfoService.isEnterpriseTenant());
    const isPersonalTenant = ref(userInfoService.isPersonalTenant());
    const isPro = ref(userInfoService.isPro());
    const isFreeUser = ref(userInfoService.isFreeUser());
    const hasCreditPackage = ref(userInfoService.hasCreditPackage());

    function syncUserInfoState() {
        currentSubscription.value = userInfoService.getCurrentSubscription();
        isLoadingSubscription.value = userInfoService.isLoadingSubscription();
        isEnterpriseTenant.value = userInfoService.isEnterpriseTenant();
        isPersonalTenant.value = userInfoService.isPersonalTenant();
        isPro.value = userInfoService.isPro();
        isFreeUser.value = userInfoService.isFreeUser();
        hasCreditPackage.value = userInfoService.hasCreditPackage();
    }

    // 订阅服务事件，同步状态到响应式变量
    disposes.push(userInfoService.onSubscriptionChange(syncUserInfoState));

    // 获取用户订阅信息
    const syncUserPackageInfo = () => userInfoService.syncUserPackageInfo();

    // 组件挂载时自动获取订阅信息
    onMounted(async () => {
        try {
            isLogin.value = await isUserLogin();
            if (isLogin.value) {
                syncUserPackageInfo();
                syncUserInfoState();
            } else {
                isLoadingSubscription.value = false;
            }
        } finally {
            isLoginResolved.value = true;
        }
    });

    return {
        isLogin,
        isLoginResolved,
        isPersonalTenant,
        isEnterpriseTenant,
        isPro,
        isFreeUser,
        hasCreditPackage,
        currentSubscription,
        isLoadingSubscription,
        syncUserPackageInfo,
    };
});
